/**
 * UI Store
 * Manages UI state including backgrounds, modals, and loading states
 */

import { create } from 'zustand';
import type { UIState } from '@/types';

interface UIStore extends UIState {
  // Actions
  setBackgroundImages: (images: string[]) => void;
  setCurrentBackgroundIndex: (index: number) => void;
  nextBackground: () => void;
  setIsCircuitModalOpen: (isOpen: boolean) => void;
  setIsFavoritesModalOpen: (isOpen: boolean) => void;
  setIsContactModalOpen: (isOpen: boolean) => void;
  setIsLoading: (isLoading: boolean, message?: string) => void;
  setLoadingMessage: (message: string) => void;
  reset: () => void;
}

const initialState: UIState = {
  backgroundImages: [],
  currentBackgroundIndex: 0,
  isCircuitModalOpen: false,
  isFavoritesModalOpen: false,
  isContactModalOpen: false,
  isLoading: false,
  loadingMessage: '',
};

/**
 * UI store hook
 */
export const useUIStore = create<UIStore>((set) => ({
  ...initialState,

  setBackgroundImages: (images) =>
    set({ backgroundImages: images, currentBackgroundIndex: 0 }),

  setCurrentBackgroundIndex: (index) =>
    set({ currentBackgroundIndex: index }),

  nextBackground: () =>
    set((state) => ({
      currentBackgroundIndex:
        (state.currentBackgroundIndex + 1) % Math.max(state.backgroundImages.length, 1),
    })),

  setIsCircuitModalOpen: (isOpen) =>
    set({ isCircuitModalOpen: isOpen }),

  setIsFavoritesModalOpen: (isOpen) =>
    set({ isFavoritesModalOpen: isOpen }),

  setIsContactModalOpen: (isOpen) =>
    set({ isContactModalOpen: isOpen }),

  setIsLoading: (isLoading, message = '') =>
    set({ isLoading, loadingMessage: message }),

  setLoadingMessage: (message) =>
    set({ loadingMessage: message }),

  reset: () => set(initialState),
}));
